/**
 * バリアント一括生成ダイアログ
 * - プリセット一括選択（業務マニュアル準拠 / SNS 主要 5 / 全 SNS）
 * - サイズプリセットを個別チェック
 * - 推定生成時間とコスト目安を表示
 * - 実行で generateAdImageVariants Callable を起動
 */
import {
  AD_SIZE_PRESETS,
  AD_SIZE_PRESET_GROUPS,
  type AdImage,
  type AdSizePresetId,
  type GenerateAdImageVariantsResult,
} from '@goenhub/shared';
import { Loader2, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { useGenerateAdImageVariants } from '@/hooks/useAdImages';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  master: AdImage;
  onCompleted?: (result: GenerateAdImageVariantsResult) => void;
}

export function VariantGenerateDialog({ open, onOpenChange, master, onCompleted }: Props) {
  const [selected, setSelected] = useState<Set<AdSizePresetId>>(() => new Set());
  const generate = useGenerateAdImageVariants();

  const selectableSizes = useMemo(
    () => AD_SIZE_PRESETS.filter((p) => p.id !== master.config.sizePresetId),
    [master.config.sizePresetId],
  );

  const toggleSize = (id: AdSizePresetId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyGroup = (groupId: typeof AD_SIZE_PRESET_GROUPS[number]['id']) => {
    const group = AD_SIZE_PRESET_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    setSelected(new Set(group.sizePresetIds.filter((id) => id !== master.config.sizePresetId)));
  };

  const clearAll = () => setSelected(new Set());

  const count = selected.size;
  const estimatedSeconds = count * 35;
  const estimatedCostUsd = (count * 0.045).toFixed(2);

  const handleSubmit = async () => {
    if (count === 0) return;
    try {
      const res = await generate.mutateAsync({
        sourceAdImageId: master.id,
        targetSizePresetIds: Array.from(selected),
      });
      const okCount = res.createdAdImageIds.length;
      const failCount = res.failures.length;
      if (failCount === 0) {
        toast.success(`${okCount} 枚のバリアントを生成しました`);
      } else {
        toast.error(`生成: ${okCount} 成功 / ${failCount} 失敗`);
      }
      onCompleted?.(res);
      onOpenChange(false);
      setSelected(new Set());
    } catch (e) {
      toast.error(`バリアント生成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // プラットフォーム別グルーピング
  const grouped = useMemo(() => {
    const map = new Map<string, typeof selectableSizes>();
    for (const p of selectableSizes) {
      const arr = map.get(p.platform) ?? [];
      arr.push(p);
      map.set(p.platform, arr);
    }
    return Array.from(map.entries());
  }, [selectableSizes]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>バリアント一括生成</DialogTitle>
          <DialogDescription>
            このマスターを起点に、選択した各サイズで新規 AdImage を生成します。コピー・配色・構図の方針は引き継がれ、配置のみ各アスペクト比に最適化されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* プリセット一括選択 */}
          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-2">
              一括選択プリセット
            </h4>
            <div className="flex flex-wrap gap-2">
              {AD_SIZE_PRESET_GROUPS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => applyGroup(g.id)}
                  className="px-3 py-1.5 rounded-sm text-xs border border-[var(--border)] hover:bg-[var(--bg-hover)] text-left"
                  title={g.description}
                >
                  <div className="font-medium text-[var(--text-primary)]">{g.label}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{g.description}</div>
                </button>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="px-3 py-1.5 rounded-sm text-xs border border-[var(--border)] hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
              >
                クリア
              </button>
            </div>
          </div>

          {/* 個別チェック */}
          <div className="max-h-[40vh] overflow-y-auto border border-[var(--border)] rounded-sm">
            {grouped.map(([platform, sizes]) => (
              <div key={platform} className="border-b border-[var(--border)] last:border-b-0">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--bg-elevated)]">
                  {platformLabel(platform)}
                </div>
                {sizes.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-[var(--bg-hover)] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSize(p.id)}
                      className="size-4"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium">{p.label}</div>
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {p.width}×{p.height} px · {p.aspectRatio}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ))}
          </div>

          {/* 概算情報 */}
          <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 px-2">
            <span>選択中: <strong className="text-[var(--text-primary)]">{count}</strong> 枚</span>
            {count > 0 && (
              <>
                <span>·</span>
                <span>推定生成時間: 約 {Math.round(estimatedSeconds / 60)} 分 {estimatedSeconds % 60 ? `${estimatedSeconds % 60} 秒` : ''}</span>
                <span>·</span>
                <span>推定コスト: 約 ${estimatedCostUsd}</span>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={generate.isPending}>
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={count === 0 || generate.isPending}
          >
            {generate.isPending ? (
              <Loader2 className="size-4 mr-1 animate-spin" />
            ) : (
              <Sparkles className="size-4 mr-1" />
            )}
            {count > 0 ? `${count} 枚生成` : '生成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function platformLabel(p: string): string {
  switch (p) {
    case 'facebook': return 'Facebook';
    case 'instagram': return 'Instagram';
    case 'instagram_story': return 'Instagram ストーリーズ';
    case 'youtube': return 'YouTube';
    case 'youtube_shorts': return 'YouTube Shorts';
    case 'line': return 'LINE';
    case 'banner': return '汎用バナー';
    default: return p;
  }
}
